
import React from "react";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderNetraaMsSQLAgent () {
    return (
      <div>
        <main className="transition-opacity duration-300">
          <div className="py-4">
            {/* Breadcrumbs */}
            <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
              <a className="hover:text-primary hover:underline" href="#">
                Netraa APM
              </a>
              <FiChevronRight />
              <span>Agent Setup Guide</span>
              <FiChevronRight />
              <span>Database</span>
              <FiChevronRight />
              <span className="text-[#1b140d] dark:text-white font-medium">MSSQL</span>
            </nav>
    
            {/* Hero */}
            <div className="relative scroll-mt-24 overflow-hidden" id="introduction">
    
              <span className="relative inline-flex items-center gap-2 px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-700 uppercase bg-white/80 border border-orange-200 rounded-full dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800/50">
                <span className="material-symbols-outlined text-[14px]">description</span>
                Documentation
              </span>
    
              <h1 className="relative text-3xl lg:text-5xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                  MSSQL Monitoring Setup Guide
                </span>
              </h1>
    
              <p className="relative text-lg text-text-muted dark:text-gray-300 mb-6 leading-relaxed">
                This document provides step-by-step instructions to configure Microsoft SQL Server (MSSQL) monitoring using sql_exporter, OpenTelemetry Collector (otelcol), and exporting metrics to the AOP Server.
              </p>
            </div>
    
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {[
                { title: "SQL Exporter", value: "MSSQL metrics & slow queries" },
                { title: "OpenTelemetry Collector", value: "Receives, processes, and forwards metrics" },
                { title: "Target", value: "AOP Server" },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="group bg-surface-light dark:bg-surface-dark p-5 rounded-2xl border border-border-color dark:border-border-dark shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <p className="text-[10px] uppercase font-bold tracking-wider text-text-muted mb-2">
                    {item.title}
                  </p>
                  <p
                    className={`text-sm font-bold leading-snug ${
                      idx === 0 ? "text-primary" : "text-text-main dark:text-white"
                    }`}
                  >
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
    
            {/* Important Note */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200/70 dark:border-amber-900/40 border-l-4 border-l-amber-500 p-5 mb-12 rounded-2xl shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-sm">
                  warning
                </span>
                <h4 className="text-sm font-bold text-amber-800 dark:text-amber-300">
                  IMPORTANT NOTE
                </h4>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                Ensure your MSSQL server is running and accessible. You will need a privileged account (e.g., sa or sysadmin) to configure the monitoring agent.
              </p>
            </div>
    
            {/* Installation Section */}
            <section className="mb-16 scroll-mt-24" id="installation">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50 shadow-sm">
                  <span className="material-symbols-outlined text-[24px]">download</span>
                </span>
                Installation Steps
              </h2>
    
              <div className="space-y-6">
                <div className="bg-white dark:bg-white/[0.02] border border-border-color dark:border-border-dark rounded-2xl p-6 shadow-sm">
                  <div className="prose dark:prose-invert max-w-none text-sm space-y-4">
                    <p>MSSQL Monitoring Setup Guide</p>
    
                    <p>
                      This document provides step-by-step instructions to configure Microsoft SQL Server (MSSQL) monitoring using sql_exporter, OpenTelemetry Collector (otelcol), and exporting metrics to the AOP Server.
                    </p>
    
                    <p>
                      SQL Exporter (for MSSQL metrics &amp; slow queries)
                      <br />
                      Collects database-level metrics from Microsoft SQL Server, such as query per second and threads.
                      <br />
                      It also helps identify slow-running queries, which is useful for performance analysis and database tuning.
                    </p>
    
                    <p>
                      OpenTelemetry Collector (otelcol)
                      <br />
                      Receives MSSQL metrics from the SQL Exporter, applies any required processing or filtering, and forwards the data to the AOP Server.
                    </p>
                  </div>
                </div>
    
                <ol className="space-y-5 text-text-muted dark:text-gray-400">
                  <li className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-color dark:border-border-dark p-5 shadow-sm">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-primary text-sm font-bold shrink-0">
                        1
                      </span>
                      <div>
                        <strong className="text-text-main dark:text-white block">
                          Create a Monitoring User in MSSQL
                        </strong>
                        <p className="mt-1 text-sm">
                          Login to SQL Server using a privileged account (e.g., sa or sysadmin) and execute:
                        </p>
                      </div>
                    </div>
                    <CodeBlock code={`CREATE LOGIN sql_exporter WITH PASSWORD = '<Password>';\nGO`} />
                  </li>
    
                  <li className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-color dark:border-border-dark p-5 shadow-sm">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-primary text-sm font-bold shrink-0">
                        2
                      </span>
                      <div>
                        <strong className="text-text-main dark:text-white block">
                          Grant Required Permissions
                        </strong>
                        <p className="mt-1 text-sm">
                          These permissions allow sql_exporter to collect metadata, performance metrics, and slow query data.
                        </p>
                      </div>
                    </div>
                    <CodeBlock code={`GRANT VIEW ANY DEFINITION TO [sql_exporter];\n-- Allows introspection of database structure for metadata and performance insights\n\nGRANT VIEW SERVER STATE TO [sql_exporter];\n-- Required to collect real-time metrics like query execution stats\n\nGRANT CONNECT ANY DATABASE TO [sql_exporter];\n-- Necessary to collect database-level metrics across all current and future databases\n\nGRANT ALTER ANY EVENT SESSION TO [sql_exporter];\n-- Required for slow query monitoring using Extended Events\nGO`} />
                  </li>
    
                 <li className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-color dark:border-border-dark p-5 shadow-sm">
  <div className="flex items-start gap-3 mb-4">
    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-primary text-sm font-bold shrink-0">
      3
    </span>
    <div>
      <strong className="text-text-main dark:text-white block">
        Configure Slow Statement Monitoring (Extended Events)
      </strong>
      <p className="mt-1 text-sm">
        Run the following script to create and start an Extended Events session for capturing slow queries:
      </p>
    </div>
  </div>

  <CodeBlock
    code={`IF EXISTS (SELECT * FROM sys.server_event_sessions WHERE name = 'QueryMonitor')
    DROP EVENT SESSION QueryMonitor ON SERVER;
GO

CREATE EVENT SESSION [QueryMonitor] ON SERVER
ADD EVENT sqlserver.rpc_completed(
    ACTION (
        sqlserver.sql_text,
        sqlserver.client_app_name,
        sqlserver.client_hostname,
        sqlserver.username,
        sqlserver.database_name,
        sqlserver.session_id
    )
    WHERE duration > 1000000 -- 1 second (microseconds)
),
ADD EVENT sqlserver.sql_batch_completed(
    ACTION (
        sqlserver.sql_text,
        sqlserver.client_app_name,
        sqlserver.client_hostname,
        sqlserver.username,
        sqlserver.database_name,
        sqlserver.session_id
    )
    WHERE duration > 1000000 -- 1 second (microseconds)
)
ADD TARGET package0.ring_buffer
(
    SET max_events_limit = 20,
        max_memory = 100
)
WITH (STARTUP_STATE = ON);

ALTER EVENT SESSION [QueryMonitor] ON SERVER STATE = START;
GO

-- Note:
-- duration is in microseconds
-- 1 second = 1,000,000
-- 60 seconds = 60,000,000
-- Adjust the threshold based on your performance requirements.`}
  />

  <p className="mt-3 text-sm">
    To record queries running longer than 60 seconds, update the condition:
    <code className="mx-1">WHERE duration &gt; 1000000 as per your requirements.</code>
  </p>
</li>
    
                  <li className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-color dark:border-border-dark p-5 shadow-sm">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-primary text-sm font-bold shrink-0">
                        4
                      </span>
                      <div>
                        <strong className="text-text-main dark:text-white block">
                          Configure sql_exporter Credentials
                        </strong>
                        <p className="mt-1 text-sm">
                          Edit the sql_exporter.yml file and set the MSSQL connection details:
                        </p>
                      </div>
                    </div>
                    <CodeBlock code={`data_source_name: 'sqlserver://sql_exporter:<Password>'@<MsSQL_host_IP>:<MsSQL_Port>'`} />
                  </li>
    
                  <li className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-color dark:border-border-dark p-5 shadow-sm">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-primary text-sm font-bold shrink-0">
                        5
                      </span>
                      <div>
                        <strong className="text-text-main dark:text-white block">
                          Run sql_exporter as a Service
                        </strong>
                      </div>
                    </div>
    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="rounded-2xl border border-border-color dark:border-border-dark p-4 bg-white dark:bg-white/[0.02]">
                        <p className="font-semibold text-text-main dark:text-white mb-2">
                          5.1 Linux (systemd)
                        </p>
                        <p className="text-sm mb-2">Create a systemd service file:</p>
                        <CodeBlock code={`sudo vi /etc/systemd/system/mssqld_exporter.service\n\nPaste the following:\n[Unit]\nDescription=Prometheus MSSQL Exporter\nAfter=network.target\n\n[Service]\nWorkingDirectory=<directory_path>/sql_exporter-0.17.3.linux-amd64\nExecStart=<ExecStart_path>/Downloads/sql_exporter-0.17.3.linux-amd64/sql_exporter --config.file=sql_exporter.yml\nRestart=always\nRestartSec=5s\n\n[Install]\nWantedBy=multi-user.target\n\nEnable and start the service:\nsudo systemctl daemon-reexec\nsudo systemctl daemon-reload\nsudo systemctl enable mssqld_exporter\nsudo systemctl restart mssqld_exporter\n\nCheck status:\nsudo systemctl status mssqld_exporter`} />
                      </div>
    
                      <div className="rounded-2xl border border-border-color dark:border-border-dark p-4 bg-white dark:bg-white/[0.02]">
                        <p className="font-semibold text-text-main dark:text-white mb-2">
                          5.2 Windows (Service using sc.exe)
                        </p>
                        <p className="text-sm mb-2">Create the Windows service:</p>
                        <CodeBlock code={`sc.exe create sql_exporter binPath= "<path_to_SQL_exporter.exe>" start= auto\n\nStart the service:\nsc start sql_exporter\n\nVerify in services.msc that sql_exporter is running. If not, start it manually from the UI.`} />
                      </div>
                    </div>
                  </li>
    
                  <li className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-color dark:border-border-dark p-5 shadow-sm">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-primary text-sm font-bold shrink-0">
                        6
                      </span>
                      <div>
                        <strong className="text-text-main dark:text-white block">
                          Configure OpenTelemetry Collector (otelcol)
                        </strong>
                        <p className="mt-1 text-sm">Create or edit otel.yaml:</p>
                      </div>
                    </div>
                    <CodeBlock code={`receivers:\n  prometheus:\n    config:\n      scrape_configs:\n        - job_name: 'mssql-monitoring'\n          scrape_interval: 15s\n          static_configs:\n            - targets: ['<IP_of_the script_running_host>:9399']\n              labels:\n                host_name: Name_to_be_seen_on_Netraa_UI\n                service_instance_id: <MsSQL_host_IP>:<MsSQL_Port>\n   \nprocessors:\n  batch:\n\nexporters:\n  otlp:\n    timeout: 60s\n    endpoint: "<AOP_address>:11800"\n    tls:\n      insecure: true\n\nservice:\n  pipelines:\n    metrics:\n      receivers: [prometheus]\n      processors: [batch]\n      exporters: [otlp]`} />
                  </li>
    
                  <li className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-color dark:border-border-dark p-5 shadow-sm">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-primary text-sm font-bold shrink-0">
                        7
                      </span>
                      <div>
                        <strong className="text-text-main dark:text-white block">
                          Run OpenTelemetry Collector as a Service
                        </strong>
                      </div>
                    </div>
    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="rounded-2xl border border-border-color dark:border-border-dark p-4 bg-white dark:bg-white/[0.02]">
                        <p className="font-semibold text-text-main dark:text-white mb-2">
                          7.1 Linux (systemd)
                        </p>
                        <p className="text-sm mb-2">Create service file:</p>
                        <CodeBlock code={`sudo vi /etc/systemd/system/otelcol.service\n\nPaste:\n[Unit]\nDescription=OpenTelemetry Collector\nAfter=network.target\n\n[Service]\nExecStart=$OTELCOL_BIN --config=$OTEL_CONFIG\nRestart=always\nRestartSec=5s\n\n[Install]\nWantedBy=multi-user.target\n\nEnable and start:\nsudo systemctl daemon-reexec\nsudo systemctl daemon-reload\nsudo systemctl enable otelcol\nsudo systemctl restart otelcol\n\nCheck status:\nsudo systemctl status otelcol`} />
                      </div>
    
                      <div className="rounded-2xl border border-border-color dark:border-border-dark p-4 bg-white dark:bg-white/[0.02]">
                        <p className="font-semibold text-text-main dark:text-white mb-2">
                          7.2 Windows (MSI Installer)
                        </p>
                        <div className="text-sm text-text-muted dark:text-gray-400 space-y-2">
                          <p>
                            1. Run the otelcol-*.msi installer included in the zip package. This registers the OpenTelemetry Collector as a Windows service.
                          </p>
                          <p>2. Navigate to:</p>
                          <CodeBlock code={`C:\\Program Files\\OpenTelemetry Collector`} />
                          <p>3. Delete the existing config.yaml (or otel-config.yaml) file.</p>
                          <p>
                            4. Copy your prepared config.yaml (e.g., from C:\\WINDOWS_MSSQL\\Otelcol\\config.yaml) into this directory.
                          </p>
                          <p>5. Modify the OTLP exporter endpoint if needed (AOP Server IP/Port).</p>
                          <p>Restart the service:</p>
                          <p>• Open services.msc</p>
                          <p>• Locate OpenTelemetry Collector</p>
                          <p>• Right-click → Stop</p>
                          <p>• Right-click → Start</p>
                        </div>
                      </div>
                    </div>
                  </li>
    
                  <li className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-color dark:border-border-dark p-5 shadow-sm">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-primary text-sm font-bold shrink-0">
                        8
                      </span>
                      <div>
                        <strong className="text-text-main dark:text-white block">
                          Validation Checklist
                        </strong>
                      </div>
                    </div>
    
                    <div className="overflow-x-auto border border-border-color dark:border-border-dark rounded-2xl">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 dark:bg-white/[0.03]">
                          <tr>
                            <th className="px-4 py-3 text-[11px] font-bold uppercase text-text-muted">
                              Component
                            </th>
                            <th className="px-4 py-3 text-[11px] font-bold uppercase text-text-muted">
                              Check Command / URL
                            </th>
                            <th className="px-4 py-3 text-[11px] font-bold uppercase text-text-muted">
                              Expected Result
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-color dark:divide-border-dark text-sm">
                          <tr className="bg-white dark:bg-transparent">
                            <td className="px-4 py-3 font-medium text-text-main dark:text-white">
                              MSSQL Login
                            </td>
                            <td className="px-4 py-3">SELECT name FROM sys.server_principals;</td>
                            <td className="px-4 py-3">sql_exporter exists</td>
                          </tr>
                          <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
                            <td className="px-4 py-3 font-medium text-text-main dark:text-white">
                              Extended Events
                            </td>
                            <td className="px-4 py-3">SELECT * FROM sys.server_event_sessions;</td>
                            <td className="px-4 py-3">QueryMonitor running</td>
                          </tr>
                          <tr className="bg-white dark:bg-transparent">
                            <td className="px-4 py-3 font-medium text-text-main dark:text-white">
                              sql_exporter
                            </td>
                            <td className="px-4 py-3">http://&lt;host&gt;:9399/metrics</td>
                            <td className="px-4 py-3">Prometheus metrics visible</td>
                          </tr>
                          <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
                            <td className="px-4 py-3 font-medium text-text-main dark:text-white">
                              otelcol
                            </td>
                            <td className="px-4 py-3">Service status</td>
                            <td className="px-4 py-3">Running</td>
                          </tr>
                          <tr className="bg-white dark:bg-transparent">
                            <td className="px-4 py-3 font-medium text-text-main dark:text-white">
                              AOP Server
                            </td>
                            <td className="px-4 py-3">UI / API</td>
                            <td className="px-4 py-3">MSSQL metrics visible</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </li>
                </ol>
              </div>
            </section>
          </div>
        </main>
      </div>
    );
    }